import type { Omit } from "utility-types"
import type { IComment } from "../Comments"
import { IContact } from "../Contacts"
import { getHotelStayDatesWithNotes, THotelNote } from "../HotelNotes"
import type { IHotel, IHotelRoomType } from "../Hotels"
import type { IHotelVoucher } from "../HotelVouchers"
import type { IPayment, IPaymentable } from "../Payments"
import type { IRoomType } from "../RoomTypes"
import type { TChildrenArray, TTourist, TTouristMember } from "../Tourists"
import { ISupplier } from "../Supplier"
import type { IUser } from "../Users"
import type { TCancellationPolicy } from "../CancellationPolicy"

import {
	getDateWiseNotes,
	IQuoteHotelExtraParams,
	IHotelExtrasBase,
	IHotelAddonBase,
	IHotelBase,
	IQuoteHotelLatestBookingStage,
	IQuoteHotelParams,
	ITrip,
} from "./../Trips/store"
import { getOrderedUniqueHotelNotes } from "../HotelPrices"
import {
	addUnit,
	dateToUTCString,
	formatDate,
	getDiff,
	isBetween,
	isSame,
	localOrUtcTimestampToLocalDate,
	parseDate,
	startOf,
	toISOString,
	dateToQuery,
	parseDateFromQuery,
} from "@sembark-travel/datetime-utils"
import { IHotelBookingStage } from "../HotelBookingStages"

import { IFilters, IFiltersInLocation } from "./utils"
import { ITripSource } from "../TripSources"
import { ILocation } from "../Locations"
import { TTripDestination } from "../TripDestinations"
import { IHotelGroup } from "../HotelGroups"

export type IBookingDetail = Omit<IBookingHotel, "hotel">

export type IExtraServiceBookingDetail = Omit<IBookingHotelExtras, "hotel">
export type IAddonBookingDetail = Omit<IBookingHotelAddon, "hotel">
export type TBookingSlot<T> = {
	date: Date
	booking?: T
}
export interface IBooking extends IPaymentable {
	id: number
	checkin: string
	checkin_local?: string
	checkout: string
	checkout_local?: string
	overall_checkin: string
	overall_checkin_local?: string
	overall_checkout: string
	overall_checkout_local?: string
	no_of_nights: number
	no_of_adults: number
	children: TChildrenArray
	pax_string: string
	no_of_children: number
	hotel_id: number
	hotel: IHotel
	booking_stage: {
		id: number
		state:
			| "initialized"
			| "in_progress"
			| "changed"
			| "booked"
			| "dropped"
			| "trip_dropped"
		/**
		 * Title case/Display name for the stage
		 */
		title: string
		tag?: string
		created_by: IUser
		created_at: string
		comments?: string
		is_before_booked: boolean
		is_same_or_after_booked: boolean
	}
	trip_id: number
	trip?: ITrip
	booked_at: string
	booking_method?: string
	change_reason?: string
	drop_reason?: string
	currency: string
	details?: Array<IBookingDetail>
	room_nights?: Array<{
		room_type: IRoomType
		no_of_rooms: number
		dates: Array<string>
		dates_local?: Array<string>
		nights: Array<number>
	}>
	extras?: Array<IExtraServiceBookingDetail>
	addons?: Array<IAddonBookingDetail>
	given_price: number
	booked_price?: number
	per_room_booked_price?: number
	per_adult_with_extra_bed_booked_price?: number
	per_child_with_extra_bed_booked_price?: number
	per_child_without_extra_bed_booked_price?: number
	booking_confirmation_id?: string
	voucher_sent_at?: string
	voucher_sent_by?: IUser
	voucher_id: string
	voucher?: IHotelVoucher
	hotel_contact?: IContact

	comments?: string
	is_shared?: boolean
	can_modify?: boolean
	can_view_prices?: boolean
	booked?: boolean
	dropped?: boolean
	payments?: Array<IPayment>
	tourists?: Array<TTourist>
	tourist?: TTourist
	members?: TTouristMember[]
	supplier?: ISupplier
	suggested_suppliers?: ISupplier[]
	supplier_contact?: IContact
}

export type TBookingCancellationCharges = {
	currency: string
	cancellation_amount: string
	cancellation_policy: TCancellationPolicy
}

export type TSelfBookedHotels = Pick<
	IBooking,
	| "id"
	| "hotel"
	| "hotel_id"
	| "overall_checkin"
	| "overall_checkin_local"
	| "overall_checkout"
	| "overall_checkout_local"
	| "no_of_nights"
	| "trip_id"
> & {
	checkin_dates: Array<string>
	name: string
	remarks?: string
}
export interface ITripHotelBooking extends Pick<
	ITrip,
	| "id"
	| "children"
	| "destinations"
	| "start_date"
	| "start_date_local"
	| "end_date"
	| "end_date_local"
	| "days"
	| "nights"
	| "trip_source"
	| "no_of_adults"
	| "no_of_children"
	| "contact"
	| "tourist"
	| "reference_id"
	| "reservations_team"
	| "paid_payments_amount"
	| "payments_amount"
	| "cancellation_reason"
	| "converted_at"
	| "converted_by"
	| "on_hold_at"
	| "on_hold_by"
> {
	package_price: number
	bookings: Array<IBooking>
	self_bookings: Array<TSelfBookedHotels>
	comments: Array<IComment>
	is_shared?: boolean
	can_modify?: boolean
	is_locked?: boolean
	can_view_prices?: boolean
	total_payments_amount: number
	total_payments_amount_currency: string
}

export const stageToDisplayNameMapping: Record<
	IBooking["booking_stage"]["state"],
	string
> = {
	initialized: "Initialized",
	in_progress: "In Progress",
	booked: "Booked",
	changed: "Changed",
	dropped: "Dropped",
	trip_dropped: "Trip Dropped",
}

const stageToDescriptionNameMapping: Record<
	IBooking["booking_stage"]["state"],
	string
> = {
	initialized: "The boooking process is just initialized",
	in_progress: "The booking is in the process",
	booked: "Hotel has been booked",
	dropped: "Hotel dropped/canceled after booking",
	changed: "Hotel changed in the booking process",
	trip_dropped: "Booking canceled after trip drop",
}

export function getDisplayNameForStage(
	stage: keyof typeof stageToDisplayNameMapping
) {
	return stageToDisplayNameMapping[stage]
}

export function getDescriptionForStage(
	stage: keyof typeof stageToDescriptionNameMapping
) {
	return stageToDescriptionNameMapping[stage]
}
export interface INotes {
	key: string
	descriptions: string[]
}

export function getNotesFromHotelBookingsDetails({
	bookingDetails,
	excludeInternals,
	tripDestinations,
}: {
	bookingDetails: IBookingDetail[] | undefined
	excludeInternals?: boolean
	tripDestinations?: TTripDestination[]
}): {
	globalHotelNotes: INotes[]
	hotelNotes: INotes[]
} {
	if ((bookingDetails?.length || 0) > 0) {
		const uniqueNotes: (THotelNote & { dates: Date[] })[] = []

		bookingDetails?.forEach((item) => {
			if (item.notes?.length) {
				getHotelStayDatesWithNotes(uniqueNotes, item.notes, [
					parseDate(item.date),
				])
			}
		})
		const voucherAndInternalNotes = uniqueNotes.filter((note) =>
			excludeInternals
				? (note.show_in_quote === 1 || note.show_in_voucher === 1) &&
					note.hotel_id !== null
				: (note.show_in_voucher ||
						(!note.show_in_quote && !note.show_in_voucher)) &&
					note.hotel_id !== null
		)
		const globalVoucherAndInternalNotes = uniqueNotes.filter((note) =>
			excludeInternals
				? (note.show_in_quote === 1 || note.show_in_voucher === 1) &&
					note.hotel_id === null
				: (note.show_in_voucher ||
						(!note.show_in_quote && !note.show_in_voucher)) &&
					note.hotel_id === null
		)

		const mealIds = [
			...new Set(bookingDetails?.flatMap((item) => item.meal_plan_id)),
		]
		const roomTypeIds = [
			...new Set(bookingDetails?.flatMap((item) => item.room_type_id)),
		]
		const globalHotelNotes = getOrderedUniqueHotelNotes({
			attachedNotes: globalVoucherAndInternalNotes,
			mealIds,
			roomTypeIds: [],
			excludeInternalNotes: excludeInternals,
			tripDestinationIds:
				tripDestinations?.map((destination) => destination.id) ?? [],
		})
		const hotelNotes = getOrderedUniqueHotelNotes({
			attachedNotes: voucherAndInternalNotes,
			mealIds,
			roomTypeIds,
			excludeInternalNotes: excludeInternals,
		})
		return {
			globalHotelNotes: Object.entries(globalHotelNotes).map(
				([key, descriptions]) => ({
					key,
					descriptions: descriptions,
				})
			),
			hotelNotes: Object.entries(hotelNotes).map(([key, descriptions]) => ({
				key,
				descriptions: descriptions,
			})),
		}
	}

	return { globalHotelNotes: [], hotelNotes: [] }
}

export interface IBookingHotel extends IHotelBase {
	given_price: number | null // Can be null in the quote-hotel-bookings table in the databse (example - if an hotel is directly added in the service bookings, it's given price will be null, if the same hotel is not avaible in the quote)
	similar_hotel_options?: Omit<Array<IBookingHotel>, "similar_hotel_options">

	// Booking-specific fields
	booked_price: number
	per_room_booked_price?: number
	per_adult_with_extra_bed_booked_price?: number
	per_child_with_extra_bed_booked_price?: number
	per_child_without_extra_bed_booked_price?: number
	booking_stages: IHotelBookingStage[]
	latest_booking_stage?: IQuoteHotelLatestBookingStage
	booked_at?: string
	booking_confirmation_id?: string
	voucher_id?: string
}

export function getEditableHotelsFromBooking(
	hotels: Array<IBookingHotel>,
	initialStartDate: Date,
	newStartDate: Date,
	durationInDays: number,
	config?: {
		disablePriceResetsUsingRates?: boolean
	}
): IQuoteHotelParams {
	function transformHotel({
		id,
		date: hotel_date,
		date_local: hotel_date_local,
		room_type,
		adults_with_extra_bed,
		children_with_extra_bed,
		children_without_extra_bed,
		no_of_rooms,
		persons_per_room,
		calculated_price,
		original_price,
		per_room_calculated_price,
		per_adult_with_extra_bed_calculated_price,
		per_child_with_extra_bed_calculated_price,
		per_child_without_extra_bed_calculated_price,
		given_price: hotel_given_price,
		per_room_given_price,
		per_adult_with_extra_bed_given_price,
		per_child_with_extra_bed_given_price,
		per_child_without_extra_bed_given_price,
		booked_price: hotel_booked_price,
		per_room_booked_price,
		per_adult_with_extra_bed_booked_price,
		per_child_with_extra_bed_booked_price,
		per_child_without_extra_bed_booked_price,
		comments,
		similar_hotel_options,
		currency,
		room_configuration,
		notes,

		//Remote-Flow-Fields
		remote_pricing_type,
		remote_price_breakdowns,
		remote_tax_strategy,
		remote_config,
		...hotel
	}: (typeof hotels)[0]) {
		const date = localOrUtcTimestampToLocalDate(hotel_date_local, hotel_date)
		const stayDayDiff = getDiff(date, initialStartDate, "days")
		const newDate =
			stayDayDiff < durationInDays - 1
				? addUnit(newStartDate, stayDayDiff, "days")
				: undefined
		let edited_given_price = config?.disablePriceResetsUsingRates
			? true
			: calculated_price !== hotel_given_price
		let edited_booked_price = config?.disablePriceResetsUsingRates
			? true
			: calculated_price !== hotel_booked_price
		let given_price = hotel_given_price
			? Number(hotel_given_price || 0)
			: undefined
		let booked_price = Number(hotel_booked_price || given_price || 0)
		let disable_price_resets = Boolean(config?.disablePriceResetsUsingRates)

		// reset the given price if date has changed
		if (newDate && !isSame(newDate, date, "day")) {
			given_price = 0
			booked_price = 0
			per_room_given_price = 0
			per_adult_with_extra_bed_given_price = 0
			per_child_with_extra_bed_given_price = 0
			per_child_without_extra_bed_given_price = 0
			per_room_booked_price = 0
			per_adult_with_extra_bed_booked_price = 0
			per_child_with_extra_bed_booked_price = 0
			per_child_without_extra_bed_booked_price = 0
			edited_given_price = false
			edited_booked_price = false
			disable_price_resets = false

			remote_pricing_type = undefined
			remote_price_breakdowns = undefined
			remote_tax_strategy = undefined
		}
		return {
			...hotel,
			dates: newDate ? [newDate] : [],
			room_type,
			adults_with_extra_bed,
			children_with_extra_bed,
			children_without_extra_bed,
			no_of_rooms,
			persons_per_room,
			notes: notes || [],
			comments: comments || "",
			date_wise_prices: newDate
				? [
						{
							date: newDate,
							currency,
							price: Number(calculated_price || 0),
							original_price: Number(original_price || 0),
							per_person_price: {
								room: Number(per_room_calculated_price || 0),
								adult: per_room_calculated_price
									? Number(per_room_calculated_price) /
										Number(persons_per_room || 1)
									: 0,
								adult_with_extra_bed: Number(
									per_adult_with_extra_bed_calculated_price || 0
								),
								child_with_extra_bed: Number(
									per_child_with_extra_bed_calculated_price || 0
								),
								child_without_extra_bed: Number(
									per_child_without_extra_bed_calculated_price || 0
								),
							},
							given_price,
							per_room_given_price,
							per_adult_given_price: per_room_given_price
								? Number(per_room_given_price) / Number(persons_per_room || 1)
								: 0,
							per_adult_with_extra_bed_given_price,
							per_child_with_extra_bed_given_price,
							per_child_without_extra_bed_given_price,
							edited_given_price,
							booked_price,
							per_room_booked_price,
							per_adult_with_extra_bed_booked_price,
							per_child_with_extra_bed_booked_price,
							per_child_without_extra_bed_booked_price,
							edited_booked_price,
							disable_price_resets,

							//Remote-Flow-Fields
							remote_pricing_type,
							remote_price_breakdowns: remote_price_breakdowns?.length
								? (remote_price_breakdowns || []).map((b) => ({
										...b,
										calculated_rate_bt: Number(b.calculated_rate_bt),
										given_rate_bt: Number(b.given_rate_bt),
										original_rate_bt: Number(b.original_rate_bt),
									}))
								: undefined,
							remote_tax_strategy,
							remote_config,
						},
					]
				: [],
		}
	}
	const hotelParams = hotels.map(({ similar_hotel_options, ...hotel }) => ({
		...transformHotel(hotel),
		similar_hotel_options: similar_hotel_options
			? similar_hotel_options.map(transformHotel)
			: [],
	}))
	return hotelParams.reduce<typeof hotelParams>((hotels, hotel) => {
		// try putting this hotel in the existing array where we have same services
		const foundIndex = hotels.findIndex(
			(h) =>
				h.hotel.id === hotel.hotel.id &&
				h.meal_plan.id === hotel.meal_plan.id &&
				h.room_type.id === hotel.room_type.id &&
				h.no_of_rooms === hotel.no_of_rooms &&
				h.adults_with_extra_bed === hotel.adults_with_extra_bed &&
				h.children_without_extra_bed === hotel.children_without_extra_bed &&
				h.children_with_extra_bed === hotel.children_with_extra_bed &&
				h.persons_per_room === hotel.persons_per_room &&
				h.comments === hotel.comments &&
				// TOOD: Check each attribute to ensure all the similar hotels are same for both hotels
				h.similar_hotel_options?.length === hotel.similar_hotel_options?.length
		)
		if (foundIndex !== -1) {
			const existingHotel = hotels[foundIndex]
			existingHotel.dates = existingHotel.dates.concat(hotel.dates)
			existingHotel.date_wise_prices = existingHotel.date_wise_prices.concat(
				hotel.date_wise_prices
			)
			if (existingHotel.similar_hotel_options?.length) {
				existingHotel.similar_hotel_options =
					existingHotel.similar_hotel_options.map((h, index) => ({
						...h,
						dates: existingHotel.dates,
						date_wise_prices: h.date_wise_prices.concat(
							hotel.similar_hotel_options[index].date_wise_prices
						),
					}))
			}
		} else {
			hotels.push(hotel)
		}
		return hotels
	}, [])
}

function _normalizeHotelsForBookingCore(hotels: IQuoteHotelParams): Array<
	Omit<IBookingHotel, "quote_id" | "similar_hotel_options"> & {
		similar_hotel_options?: Array<
			Omit<IBookingHotel, "quote_id" | "similar_hotel_options">
		>
		fetching_prices?: boolean
	}
> {
	type T_Booking_Hotel = Omit<
		IBookingHotel,
		"quote_id" | "similar_hotel_options"
	> & {
		similar_hotel_options?: Array<
			Omit<IBookingHotel, "quote_id" | "similar_hotel_options">
		>
		fetching_prices?: boolean
	}
	return hotels.reduce<Array<T_Booking_Hotel>>(function transformHotel(
		hotels,
		{
			dates,
			fetching_prices,
			date_wise_prices = [],
			similar_hotel_options,
			notes,
			...otherData
		}
	) {
		dates.forEach((date, i) => {
			const {
				currency,
				price,
				original_price,
				given_price,
				booked_price,
				per_room_given_price,
				per_person_price,
				per_adult_with_extra_bed_given_price,
				per_child_with_extra_bed_given_price,
				per_child_without_extra_bed_given_price,
				per_room_booked_price,
				per_adult_with_extra_bed_booked_price,
				per_child_with_extra_bed_booked_price,
				per_child_without_extra_bed_booked_price,
				remote_pricing_type,
				remote_price_breakdowns,
				remote_tax_strategy,
				remote_config,
			} = date_wise_prices[i] ||
				date_wise_prices[0] || {
					currency: "INR",
					price: 0,
					original_price: 0,
					given_price: 0,
					booked_price: 0,
					per_room_given_price: 0,
					per_adult_with_extra_bed_given_price: 0,
					per_child_with_extra_bed_given_price: 0,
					per_child_without_extra_bed_given_price: 0,
					per_room_booked_price: 0,
					per_adult_with_extra_bed_booked_price: 0,
					per_child_with_extra_bed_booked_price: 0,
					per_child_without_extra_bed_booked_price: 0,
				}
			const hotelStayDateISO = toISOString(startOf(date, "day"))
			const dateWiseNotes = getDateWiseNotes(notes, date)
			hotels.push({
				...otherData,
				similar_hotel_options: similar_hotel_options?.length
					? similar_hotel_options
							.reduce<Array<T_Booking_Hotel>>(transformHotel, [])
							.filter((h) => h.date === hotelStayDateISO)
					: undefined,
				id: Math.random(),
				hotel_id: otherData.hotel.id,
				meal_plan_id: otherData.meal_plan.id,
				room_type_id: otherData.room_type.id,
				note_ids: dateWiseNotes.map((n) => n.id),
				notes: dateWiseNotes,
				date: hotelStayDateISO,
				currency,

				//---CALCULATED-PRICE-START---
				calculated_price: price,
				original_price: Number(original_price || 0),
				per_room_calculated_price: per_person_price?.room || undefined,
				per_adult_with_extra_bed_calculated_price:
					per_person_price?.adult_with_extra_bed || undefined,
				per_child_with_extra_bed_calculated_price:
					per_person_price?.child_with_extra_bed || undefined,
				per_child_without_extra_bed_calculated_price:
					per_person_price?.child_without_extra_bed || undefined,
				//---CALCULATED-PRICE-END---

				//---GIVEN-PRICE-START---
				given_price: given_price ?? null, // Booking flow: undefined becomes null (incase if the hotel is directly added to the booking or user made changes in the config which might not available in the quote)
				per_room_given_price: per_room_given_price || undefined,
				per_adult_with_extra_bed_given_price:
					per_adult_with_extra_bed_given_price || undefined,
				per_child_with_extra_bed_given_price:
					per_child_with_extra_bed_given_price || undefined,
				per_child_without_extra_bed_given_price:
					per_child_without_extra_bed_given_price || undefined,
				//---GIVEN-PRICE-END---

				//---Booked-PRICE-START---
				booked_price: booked_price || 0,
				per_room_booked_price: per_room_booked_price || undefined,
				per_adult_with_extra_bed_booked_price:
					per_adult_with_extra_bed_booked_price || undefined,
				per_child_with_extra_bed_booked_price:
					per_child_with_extra_bed_booked_price || undefined,
				per_child_without_extra_bed_booked_price:
					per_child_without_extra_bed_booked_price || undefined,
				//---Booked-PRICE-END---

				comments: otherData.comments || "",
				booking_stages: [],
				fetching_prices: Boolean(fetching_prices),

				//Remote-Flow-fields

				remote_pricing_type,
				remote_price_breakdowns,
				remote_tax_strategy,
				remote_config,
			})
		})
		return hotels
	}, [])
}

/**
 * Normalizes hotels for BOOKING FLOW.
 * - undefined given_price becomes null
 *
 * @param hotels - Array of hotel parameters to normalize
 */
export function normalizeHotelsForBooking(
	hotels: IQuoteHotelParams
): ReturnType<typeof _normalizeHotelsForBookingCore> {
	return _normalizeHotelsForBookingCore(hotels)
}

/**
 * Transforms hotels to request data for BOOKING FLOW.
 */
export function transformHotelsToRequestDataForBooking(
	hotels: IQuoteHotelParams
) {
	const normalizedHotels = normalizeHotelsForBooking(hotels)
	function transform({
		date,
		hotel,
		meal_plan,
		room_type,
		notes,
		similar_hotel_options,
		persons_per_room,
		no_of_rooms,
		adults_with_extra_bed,
		children_with_extra_bed,
		children_without_extra_bed,
		currency,
		//--CALCULATED-PRICE-START
		calculated_price,
		per_room_calculated_price,
		per_adult_with_extra_bed_calculated_price,
		per_child_with_extra_bed_calculated_price,
		per_child_without_extra_bed_calculated_price,
		//--CALCULATED-PRICE-END

		//--GIVEN-PRICE-START
		given_price,
		per_room_given_price,
		per_adult_with_extra_bed_given_price,
		per_child_with_extra_bed_given_price,
		per_child_without_extra_bed_given_price,
		//--GIVEN-PRICE-END

		//--Booked-PRICE-START
		booked_price,
		per_room_booked_price,
		per_adult_with_extra_bed_booked_price,
		per_child_with_extra_bed_booked_price,
		per_child_without_extra_bed_booked_price,
		//--Booked-PRICE-END

		comments,
	}: (typeof normalizedHotels)[0]) {
		return {
			date: dateToUTCString(date),
			date_local: formatDate(date, "YYYY-MM-DD"),
			hotel_id: hotel.id,
			meal_plan_id: meal_plan.id,
			room_type_id: room_type.id,
			note_ids: notes?.map((n) => n.id),
			destination_ids: hotel.trip_destinations?.map(
				(destination) => destination.id
			),
			persons_per_room,
			no_of_rooms,
			adults_with_extra_bed,
			children_with_extra_bed,
			children_without_extra_bed,
			currency,
			//--CALCULATED-PRICE-START
			calculated_price,
			per_room_calculated_price,
			per_adult_with_extra_bed_calculated_price,
			per_child_with_extra_bed_calculated_price,
			per_child_without_extra_bed_calculated_price,
			//--CALCULATED-PRICE-END

			//--GIVEN-PRICE-START
			given_price,
			per_room_given_price,
			per_adult_with_extra_bed_given_price,
			per_child_with_extra_bed_given_price,
			per_child_without_extra_bed_given_price,
			//--GIVEN-PRICE-END

			//--Booked-PRICE-START
			booked_price,
			per_room_booked_price,
			per_adult_with_extra_bed_booked_price,
			per_child_with_extra_bed_booked_price,
			per_child_without_extra_bed_booked_price,
			//--Booked-PRICE-END
			comments,
			similar_hotel_options,
		}
	}
	return normalizedHotels.map(({ similar_hotel_options, ...hotel }) => ({
		...transform(hotel),
		similar_hotel_options: similar_hotel_options?.length
			? similar_hotel_options.map(transform)
			: [],
	}))
}

export interface IBookingHotelExtras extends IHotelExtrasBase {
	given_price: number | null
	booked_price: number
}

export interface IBookingHotelAddon extends IHotelAddonBase {
	given_price: number
	booked_price: number
}

export function getEditableHotelExtrasFromBooking(
	extras: Array<IBookingHotelExtras>,
	initialStartDate: Date,
	newStartDate: Date,
	durationInDays: number
): IQuoteHotelExtraParams {
	return extras.map(
		({ given_price, date, date_local, comments, ...others }) => {
			let newDate: Date | undefined = undefined
			if (date) {
				const stayDayDiff = getDiff(
					localOrUtcTimestampToLocalDate(date_local, date),
					initialStartDate,
					"days"
				)
				// remove the dates if it is not withing the duration range
				newDate =
					stayDayDiff < durationInDays
						? startOf(addUnit(newStartDate, stayDayDiff, "days"), "day")
						: undefined
			}
			return {
				...others,
				price: given_price ? Number(given_price || 0) : undefined,
				date: date ? newDate : undefined,
				comments: comments || "",
			}
		}
	)
}

function _normalizeHotelExtrasForBookingCore(
	hotelExtras: IQuoteHotelExtraParams
): Array<Omit<IBookingHotelExtras, "quote_id">> {
	return hotelExtras.map(
		({ date, price, comments, booked_price, ...otherData }) => {
			return {
				...otherData,
				id: Math.random(),
				date: date ? toISOString(startOf(date, "day")) : "",
				given_price: price ?? null, // Booking flow: undefined becomes null
				comments: comments || "",
				booked_price: Number(booked_price || 0),
			}
		}
	)
}

/**
 * Normalizes hotel extras for BOOKING FLOW.
 * - undefined given_price becomes null
 *
 * @param hotelExtras - Array of hotel extra parameters to normalize
 */
export function normalizeHotelExtrasForBooking(
	hotelExtras: IQuoteHotelExtraParams
): ReturnType<typeof _normalizeHotelExtrasForBookingCore> {
	return _normalizeHotelExtrasForBookingCore(hotelExtras)
}

/**
 * Transforms hotel extras to request data for BOOKING FLOW.
 */
export function transformHotelExtrasToRequestDataForBooking(
	hotelExtras: IQuoteHotelExtraParams
) {
	return normalizeHotelExtrasForBooking(hotelExtras).map(
		({
			date,
			service,
			hotel,
			given_price,
			currency,
			calculated_price,
			booked_price,
			comments,
		}) => ({
			date: date ? dateToUTCString(date) : "",
			date_local: date ? formatDate(date, "YYYY-MM-DD") : "",
			service: service.name,
			hotel_id: hotel?.id,
			currency,
			calculated_price,
			booked_price,
			comments,
			price: given_price,
		})
	)
}

export type TClosedStatus = "stop_sale" | "blackout" | null

export function getRoomStatusOnDate(
	room_type: Pick<IHotelRoomType, "black_out_date_intervals">,
	date: Date
): TClosedStatus {
	const intervals = room_type.black_out_date_intervals ?? []
	let hasBlackout = false
	for (const interval of intervals) {
		const start = parseDate(interval.start_date, "YYYY-MM-DD")
		const end = parseDate(interval.end_date, "YYYY-MM-DD")

		if (!isBetween(date, start, end, undefined, "[]")) continue
		if (interval.stop_sales) {
			return "stop_sale"
		}
		hasBlackout = true
	}
	return hasBlackout ? "blackout" : null
}

export function getCloseDataForHotels(
	room_type: IHotelRoomType,
	dates: Date[],
	label: string,
	closeData: Record<string, Date[]>
): Record<string, Date[]> {
	for (const date of dates || []) {
		const status = getRoomStatusOnDate(
			{ black_out_date_intervals: room_type.black_out_date_intervals },
			date
		)
		if (status === "stop_sale") {
			if (!closeData[label]) {
				closeData[label] = []
			}
			closeData[label].push(date)
		}
	}
	return closeData
}

//------------------For Hotel Operational Calendar Filters------------------

export function filtersToLocationQuery(filters: IFilters): IFiltersInLocation {
	const {
		q,
		locations,
		stay_after,
		stay_before,
		overall_checkin_after,
		overall_checkin_before,
		overall_checkout_after,
		overall_checkout_before,
		hotels,
		hotel_groups,
		booking_tags,
		booking_stages,
		payment_statuses,
		trip_sources,
		trip_owners,
		owners,
		status,
		trip_destinations,
		date,
	} = filters

	const query: IFiltersInLocation = {}

	if (status) query.status = status
	if (q) query.q = q

	if (stay_after) {
		query.sa = dateToQuery(stay_after)
	}

	if (stay_before) {
		query.sb = dateToQuery(stay_before)
	}

	if (overall_checkin_after) {
		query.oca = dateToQuery(overall_checkin_after)
	}

	if (overall_checkin_before) {
		query.ocb = dateToQuery(overall_checkin_before)
	}

	if (overall_checkout_after) {
		query.oda = dateToQuery(overall_checkout_after)
	}

	if (overall_checkout_before) {
		query.odb = dateToQuery(overall_checkout_before)
	}

	if (booking_stages?.length)
		query.booking_stages = booking_stages.map((t) => `${t.id}_${t.name}`)

	if (payment_statuses?.length)
		query.payment_statuses = payment_statuses.map((t) => `${t.id}_${t.name}`)

	if (trip_owners?.length)
		query.trip_owners = trip_owners.map((t) => `${t.id}_${t.name}`)

	if (booking_tags?.length)
		query.booking_tags = booking_tags.map((t) => `${t.id}_${t.name}`)

	if (trip_sources?.length)
		query.trip_sources = trip_sources.map((t) => `${t.id}_${t.name}`)

	if (owners?.length) query.owners = owners.map((t) => `${t.id}_${t.name}`)

	if (locations?.length)
		query.locations = locations.map((t) => `${t.id}_${t.name}`)

	if (trip_destinations?.length)
		query.trip_destinations = trip_destinations.map((t) => `${t.id}_${t.name}`)

	if (hotels?.length) query.hotels = hotels.map((t) => `${t.id}_${t.name}`)

	if (hotel_groups?.length)
		query.hotel_groups = hotel_groups.map((t) => `${t.id}_${t.name}`)

	if (date) query.date = dateToQuery(date)

	return query
}

export function locationQueryToFilters(query: IFiltersInLocation): IFilters {
	const {
		q,
		locations,
		sa,
		sb,
		oca,
		ocb,
		oda,
		odb,
		hotels,
		hotel_groups,
		status,
		booking_tags,
		booking_stages,
		payment_statuses,
		trip_sources,
		trip_owners,
		owners,
		trip_destinations,
		date,
	} = query

	const filters: IFilters = {}

	if (status) filters.status = status
	if (q) filters.q = q

	if (sa) filters.stay_after = parseDateFromQuery(sa)

	if (sb) filters.stay_before = parseDateFromQuery(sb)

	if (oca) filters.overall_checkin_after = parseDateFromQuery(oca)

	if (ocb) filters.overall_checkin_before = parseDateFromQuery(ocb)

	if (oda) filters.overall_checkout_after = parseDateFromQuery(oda)

	if (odb) filters.overall_checkout_before = parseDateFromQuery(odb)

	if (booking_stages?.length)
		filters.booking_stages = booking_stages.map((t) => {
			const slices = t.split("_")

			return {
				id: parseInt(slices[0]),
				name: slices.slice(1).join("_"),
			}
		}) as Array<{ id: number; name: string }>

	if (payment_statuses?.length)
		filters.payment_statuses = payment_statuses.map((t) => {
			const slices = t.split("_")

			return {
				id: parseInt(slices[0]),
				name: slices.slice(1).join("_"),
			}
		}) as Array<{ id: number; name: string }>

	if (trip_owners?.length)
		filters.trip_owners = trip_owners.map((t) => {
			const slices = t.split("_")

			return {
				id: parseInt(slices[0]),
				name: slices.slice(1).join("_"),
			}
		}) as Array<IUser>

	if (booking_tags?.length)
		filters.booking_tags = booking_tags.map((t) => {
			const slices = t.split("_")

			return {
				id: parseInt(slices[0]),
				name: slices.slice(1).join("_"),
			}
		}) as Array<{ id: number; name: string }>

	if (trip_sources?.length)
		filters.trip_sources = trip_sources.map((t) => {
			const slices = t.split("_")

			return {
				id: parseInt(slices[0]),
				name: slices.slice(1).join("_"),
			}
		}) as Array<ITripSource>

	if (locations?.length)
		filters.locations = locations.map((t) => {
			const slices = t.split("_")

			return {
				id: parseInt(slices[0]),
				name: slices.slice(1).join("_"),
			}
		}) as Array<ILocation>

	if (trip_destinations?.length)
		filters.trip_destinations = trip_destinations.map((d) => {
			const slices = d.split("_")

			return {
				id: parseInt(slices[0]),
				name: slices.slice(1).join("_"),
			}
		}) as Array<TTripDestination>

	if (hotels?.length)
		filters.hotels = hotels.map((t) => {
			const slices = t.split("_")

			return {
				id: parseInt(slices[0]),
				name: slices.slice(1).join("_"),
			}
		}) as Array<IHotel>

	if (hotel_groups?.length)
		filters.hotel_groups = hotel_groups.map((t) => {
			const slices = t.split("_")

			return {
				id: parseInt(slices[0]),
				name: slices.slice(1).join("_"),
			}
		}) as Array<IHotelGroup>

	if (owners?.length)
		filters.owners = owners.map((t) => {
			const slices = t.split("_")

			return {
				id: parseInt(slices[0]),
				name: slices.slice(1).join("_"),
			}
		}) as Array<IUser>

	if (date) filters.date = parseDateFromQuery(date)

	return filters
}
//--------------------------------------
